/// <amd-module name="VSS/Features/Charts/Internal/PieChartImplementation" />
import * as Highcharts from "highcharts";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { TooltipFormatterPoint } from "VSS/Features/Charts/Internal/TooltipFormatter";
import * as Charts_Resources from "VSS/Features/Charts/Resources";
import { IVssThemeService } from "VSS/Platform/Theming";
import { getFriendlyDisplayValue } from "./FriendlyNumberFormatter";

import { ChartTypesRegistry } from "./ChartTypesRegistry";
import { CommonChartImplementation } from "./CommonChartImplementation";

export class PieChartImplementation extends CommonChartImplementation {
    /* Expose the neccessary set of plot option defaults.*/

    constructor() {
        super();
        this._injectAxisNames = true;
    }

    public isOneDimensional(): boolean {
        return true;
    }

    public hasLegendByDefault(): boolean {
        return true;
    }

    public getTooltipHeaderValue(formatterPoint: TooltipFormatterPoint): string | number {
        // The point doesn't have a category but rather a name
        return formatterPoint.point.name;
    }

    public translateOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.Options {
        const options = super.translateOptions(chartOptions);

        // Provide centered label, if the consumer has opted into it.
        const that: PieChartImplementation = this; // For chart load function
        const piechartOptions = chartOptions.specializedOptions as Charts_Contracts.PieChartOptions;
        options.chart.events = {
            load: function(event: Event) {
                that.onLoad(this, this.renderer, piechartOptions);
            }
        };

        options.plotOptions.pie.dataLabels = options.plotOptions.pie.dataLabels || {};

        // We will need to update this logic for setting the formatter if we ever encounter a case where we need multiple labels per data point
        if (!Array.isArray(options.plotOptions.pie.dataLabels)) {
            options.plotOptions.pie.dataLabels.formatter = function() {
                if (this.percentage > 2) {
                    const fixedValue = +(this.y as Number).toFixed(2);
                    return getFriendlyDisplayValue(fixedValue);
                }
                return "";
            };
        } else {
            throw Error("Expected single dataLabels configuration, not an array");
        }

        return options;
    }

    public onLoad(chart: any, renderer: Highcharts.SVGRenderer, pieChartOptions?: Charts_Contracts.PieChartOptions) {
        if (pieChartOptions && pieChartOptions.showLabels) {
            let total = 0;

            // Tabulate total value for this chart.
            for (let i = 0, len = chart.series[0].yData.length; i < len; i++) {
                total += chart.series[0].yData[i];
            }

            // Determine position, assuming default positioning.
            let x = chart.plotLeft + chart.plotWidth * 0.5;
            let y = chart.plotTop + chart.plotHeight * 0.5;

            // Override if user customized.
            if (pieChartOptions.center) {
                // This is a magic number for correcting inaccuracy of calculated position when given absolute center.
                // It's reliable across browsers, but not well correlated to text block.
                const arbitraryFixedPositioningOffset = 20;
                if (pieChartOptions.center[0]) {
                    x = chart.plotLeft + pieChartOptions.center[0] + arbitraryFixedPositioningOffset;
                }
                if (pieChartOptions.center[1]) {
                    y = chart.plotTop + pieChartOptions.center[1] + arbitraryFixedPositioningOffset;
                }
            }

            // Due to the limited 4 digit space available to total, format as a rounded total, with an SI Suffix.
            // Values < 1 will show as 0.
            const formattedValue = getFriendlyDisplayValue(Math.round(total));
            const fontSize = this.getfontSize(formattedValue.length);
            const label = renderer.text(formattedValue, x, y).add();

            const themeService = this._chartOptions.pageContext.getService<IVssThemeService>("IVssThemeService");
            const pieChartCenterColor = themeService ? themeService.getThemeValue("text-primary-color") : "#222222";

            label.attr({
                class: "pie-chart-center-value",
                title: total
            });
            label.css({
                "font-size": fontSize,
                color: pieChartCenterColor
            } as Highcharts.CSSObject);

            // After rendering, determine how much space is occupied, and re-position the label
            const labelBox = label.getBBox();
            x -= labelBox.width * 0.5;
            y += labelBox.height * 0.25;
            label.attr({ x: x, y: y });
        }

        if (pieChartOptions == null || pieChartOptions.showLabels) {
            this.updateDataLabelDistance(chart);
        }
    }

    public translatePlotOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.PlotOptions {
        const plotOptions: Highcharts.PlotOptions = super.translatePlotOptions(chartOptions);

        plotOptions.pie = {
            size: "100%",
            innerSize: "50%",

            // Show plain text value labels over the pie slices.
            dataLabels: {
                enabled: !(
                    (chartOptions.specializedOptions as Charts_Contracts.PieChartOptions) &&
                    !(chartOptions.specializedOptions as Charts_Contracts.PieChartOptions).showLabels
                ),
                style: {
                    textOutline: false,
                    fontWeight: "normal"
                },
                formatter: function() {
                    return getFriendlyDisplayValue(this.point.value);
                }
            } as any,

            // Show the legend, which is suppressed by default in Highcharts pie
            showInLegend: true,

            states: {
                hover: { enabled: false } // Prevent styling a slice differently when hovered over
            },
            point: {
                events: {
                    click: () => {
                        return true;
                    },
                    legendItemClick: (event: Highcharts.PointLegendItemClickEventObject) => {
                        return this.onLegendClick(event);
                    }
                }
            }
        };

        // Override Pie chart defaults with user customization.
        const pieChartOptions = chartOptions.specializedOptions as Charts_Contracts.PieChartOptions;
        if (pieChartOptions) {
            if (pieChartOptions.innerSize) {
                plotOptions.pie.innerSize = pieChartOptions.innerSize;
            }
            if (pieChartOptions.size) {
                plotOptions.pie.size = pieChartOptions.size;
            }
            if (pieChartOptions.center) {
                plotOptions.pie.center = pieChartOptions.center.map(value => {
                    return value != null ? value.toString() : null;
                }) as any;
            }
        }

        return plotOptions;
    }

    public translateTooltipOptions(tooltipOptions: Charts_Contracts.TooltipOptions): Highcharts.TooltipOptions {
        const translatedOptions = super.translateTooltipOptions(tooltipOptions);

        // In version 5.0.12 of highcharts, setting shared to true, our framework's default, causes the tooltip to not show for pie.
        if (tooltipOptions == null || tooltipOptions.onlyShowFocusedSeries == null) {
            translatedOptions.shared = false;
        }

        return translatedOptions;
    }

    /** Calculates where to place pie slice data labels so they are shown in the center of the slices. */
    private updateDataLabelDistance(chart: any) {
        const shapeArgs = chart.series[0].points[0].shapeArgs;
        const radius = shapeArgs.r;
        const innerRadius = shapeArgs.innerR;
        const distance = (radius - innerRadius) / 2;

        const series = chart.series[0];
        series.options.dataLabels = { distance: -distance };
        series.update(series.options);
    }

    // Get the size that would be best fit the string into the pie.
    private getfontSize(stringLength: number): string {
        let fontSize = "36px";
        if (stringLength > 6) {
            fontSize = "24px";
        } else if (stringLength > 4) {
            fontSize = "30px;";
        }
        return fontSize;
    }
}

ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Pie,
    constructor: PieChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Pie
});
